import React, { useState, useEffect } from "react";
import { Modal, Upload, message, Image, Button, Space } from "antd";
import { PlusOutlined, CheckOutlined } from "@ant-design/icons";
import { uploadImage } from "../../../../api/service";

const ImageBankSelector = ({ 
  value, 
  onChange, 
  defaultImages = [],
  onImageUpload 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageBank, setImageBank] = useState(defaultImages || []);
  const [selectedImage, setSelectedImage] = useState(value || null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Initialize with default images if bank is empty
    if (imageBank.length === 0 && defaultImages.length > 0) {
      setImageBank(defaultImages);
    }
  }, [defaultImages]);

  useEffect(() => {
    setSelectedImage(value);
  }, [value]);

  const beforeUpload = (file) => {
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    const isLt1M = file.size / 1024 / 1024 <= 1;

    if (!isJpgOrPng) {
      message.error({
        content: 'You can only upload JPG, PNG, or Webp file formats!',
        style: { marginTop: '20px' },
        duration: 4,
      });
      return false;
    }
    
    if (!isLt1M) {
      const actualSize = (file.size / 1024 / 1024).toFixed(2);
      message.error({
        content: `Image size (${actualSize} MB) exceeds the 1 MB limit. Please select a smaller one.`,
        style: { marginTop: '20px' },
        duration: 4,
      });
      return false;
    }
    
    return true;
  };

  const handleImageUpload = async (info) => {
    try {
      const { file } = info;
      
      if (!beforeUpload(file)) {
        return;
      }

      setUploading(true);
      const formData = new FormData();
      formData.append("image", file);
      const response = await uploadImage(formData);
      
      if (response && response.url) {
        const newImage = {
          url: response.url,
          id: Date.now().toString(), // Temporary ID
          isDefault: false,
        };
        
        // Add to image bank
        const updatedBank = [...imageBank, newImage];
        setImageBank(updatedBank);
        
        // Auto-select the newly uploaded image
        setSelectedImage(response.url);
        onChange && onChange(response.url);
        
        // Notify parent component about new image
        if (onImageUpload) {
          onImageUpload(newImage);
        }
        
        message.success("Image uploaded and added to bank");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      message.error("Error while uploading image");
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (imageUrl) => {
    setSelectedImage(imageUrl);
    onChange && onChange(imageUrl);
    setIsModalOpen(false);
  };

  return (
    <div>
      <div className="flex items-center gap-4">
        {selectedImage ? (
          <div className="relative">
            <Image
              src={selectedImage}
              alt="Selected"
              width={120}
              height={80}
              style={{ objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb" }}
              preview={false}
            />
            <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 shadow-md">
              <CheckOutlined className="text-white text-xs" />
            </div>
          </div>
        ) : (
          <div className="w-[120px] h-[80px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 text-sm">
            No image selected
          </div>
        )}
        <Button 
          type="primary" 
          onClick={() => setIsModalOpen(true)}
          icon={<PlusOutlined />}
          size="middle"
        >
          {selectedImage ? "Change Image" : "Select Image"}
        </Button>
      </div>

      <Modal
        title={
          <div className="text-lg font-semibold">Select Image from Bank</div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={900}
        styles={{
          body: {
            padding: '24px',
          }
        }}
      >
        <div className="space-y-6">
          {/* Upload Section */}
          <div className="border-b border-gray-200 pb-6">
            <h4 className="text-base font-semibold mb-4 text-gray-700">Upload New Image</h4>
            <div className="flex items-start">
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleImageUpload}
                accept="image/jpeg,image/png,image/webp"
                disabled={uploading}
                className="upload-section"
              >
                {uploading ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-gray-500">Uploading...</div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <PlusOutlined className="text-2xl text-gray-400 mb-2" />
                    <div className="text-sm text-gray-500">Upload</div>
                  </div>
                )}
              </Upload>
            </div>
          </div>

          {/* Image Bank Section */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-gray-700">Image Bank</h4>
            {imageBank.length > 0 ? (
              <div className="grid grid-cols-4 gap-4 max-h-[450px] overflow-y-auto pr-2 pb-2">
                {imageBank.map((image, index) => (
                  <div
                    key={image.id || image.url || index}
                    className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all group ${
                      selectedImage === image.url
                        ? "border-blue-500 ring-2 ring-blue-200 shadow-md"
                        : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                    }`}
                    onClick={() => handleImageSelect(image.url)}
                    style={{ aspectRatio: '1' }}
                  >
                    <Image
                      src={image.url}
                      alt={`Image ${index + 1}`}
                      width="100%"
                      height="100%"
                      style={{ objectFit: "cover", display: "block" }}
                      preview={false}
                    />
                    {selectedImage === image.url && (
                      <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1.5 shadow-lg z-10">
                        <CheckOutlined className="text-white text-sm" />
                      </div>
                    )}
                    {image.isDefault && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs py-1.5 px-2 text-center font-medium z-10">
                        Default
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-12 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                <div className="text-sm font-medium mb-1">No images in bank</div>
                <div className="text-xs">Upload an image above to get started</div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ImageBankSelector;
